import React from 'react';
import Overlay from '../Overlay';
import SimpleTable from '../tables/SimpleTable';
import FilterCard, { FIELD_TYPE, ReactSelect } from '../FilterCard';
import {
  formatCurrencyWithoutDecimals,
  getBoolSelectOptionsWithInts,
  getBoolSelectVal,
  getLabels,
  getNumPages,
  getPageOptions,
  hideModal,
  showModalNoOutsideClick,
  slicePages,
} from '../../util/FormatUtil';
import { sweetalert } from '../../App';
import Select from 'react-select';
import { Result } from '../../types/Result';
import { ResultsManagementModal } from '../modals/ResultsManagementModal';
import NetworkUtil from '../../network/NetworkUtil';
import ResultsAPI from '../../network/ResultsAPI';
import { Column } from '../tables/TableBase';
import { Sorter, TableOrder } from '../../sorting/Sorter';
import PaginationTool from '../PaginationTool';
import SystemAPI from '../../network/SystemAPI';
import { AdminPages } from './AdminNav';
import { Product } from '../../types/Product';
import ShopAPI from '../../network/ShopAPI';
import ProductManagementModal from '../modals/ProductManagementModal';
import AdminAPI from '../../network/AdminAPI';
import ServicesAPI from '../../network/ServicesAPI';

const ITEMS_PER_PAGE = 25;

interface ProductManagementState {
  showLoading: boolean;
  selectedProduct: Product;
  resultQueryProducts;
  tableData: {}[];
  filter?: {
    ID;
    IsHomeKit?;
  };
  direction?: TableOrder;
  page_options: ReactSelect[];
  selected_page?: { label; value };
  products: { label; value }[];
  pageCustomQuestions?;
  services?;
}

export default class ProductManagement extends React.Component<any, ProductManagementState> {
  constructor(props) {
    super(props);

    this.state = {
      showLoading: false,
      selectedProduct: {} as Product,
      tableData: [] as any,
      resultQueryProducts: [] as any,
      filter: { ID: [], IsHomeKit: undefined },
      direction: 'asc',
      page_options: [{ label: '1', value: '1' }],
      selected_page: { label: '1', value: 1 },
      products: [],
      pageCustomQuestions: [],
      services: [],
    };

    this.createOrModifyProduct = this.createOrModifyProduct.bind(this);
  }

  componentDidMount() {
    document.title = 'Products Management Page';
    this.loadProducts();
    this.queryProductsData(1);
    this.loadServices();
  }

  loadProducts() {
    this.setState({ showLoading: true }, async () => {
      const products = await ShopAPI.getProducts();

      this.setState({
        //@ts-ignore
        products: products,
        showLoading: false,
      });
    });
  }

  loadServices() {
    this.setState({ showLoading: true }, async () => {
      const services = await ServicesAPI.getAllServices();

      this.setState({
        //@ts-ignore
        services: services.data,
        showLoading: false,
      });
    });
  }

  queryProductsData(page: number) {
    this.setState({ showLoading: true }, async () => {
      let response = await ShopAPI.filterProducts({
        filter: {
          ID: this.state.filter.ID,
          IsHomeKit: this.state.filter.IsHomeKit,
        },
      });

      if (response.data.length < 1) {
        this.setState({ showLoading: false, tableData: [], resultQueryProducts: null });
        return sweetalert.fire({ title: '', text: 'No Products returned', icon: 'info' });
      }
      if (!response.success) {
        return sweetalert.fire({
          title: '',
          text: 'Unable to filter Products data at this time',
          icon: 'error',
        });
      }

      this.setState({
        tableData: slicePages(response.data, page, ITEMS_PER_PAGE),
        resultQueryProducts: response.data,
        page_options: getPageOptions(getNumPages(response.data, ITEMS_PER_PAGE)),
        showLoading: false,
      });
    });
  }

  createOrModifyProduct(product: Product) {
    this.setState({ showLoading: true }, async () => {
      if (product.ID === null) {
        let response = await ShopAPI.createProduct(product);
        if (!response.success) {
          let msg = 'Unable to create Product at this time.';
          if (response.reason) {
            msg = response.reason;
          }
          return sweetalert
            .fire({
              icon: 'error',
              title: '',
              text: msg,
              allowOutsideClick: false,
            })
            .then((response) => {
              if (response.isConfirmed) {
                window.location.reload();
              }
            });
        }
      } else {
        let response = await ShopAPI.editProduct(product);
        if (!response.success) {
          let msg = 'Unable to edit Product at this time.';
          if (response.reason) {
            msg = response.reason;
          }
          return sweetalert
            .fire({
              icon: 'error',
              title: '',
              text: msg,
              allowOutsideClick: false,
            })
            .then((response) => {
              if (response.isConfirmed) {
                window.location.reload();
              }
            });
        }
      }

      sweetalert.fire({ icon: 'success', title: '', text: 'Product saved' }).then(() => {
        this.setState(
          {
            selectedProduct: {
              ID: null,
              Name: null,
              Price: null,
              Description: null,
              Units: null,
            } as Product,
          },
          () => {
            this.loadProducts();
            this.queryProductsData(this.state.selected_page.value);
          }
        );
      });
    });
  }

  handleExportToCSV() {
    this.setState({ showLoading: true }, async () => {
      await NetworkUtil.downloadCSV('/api/admin/shop/products/csv', 'Product Management Data.xlsx', {
        filter: this.state.filter,
      });
      this.setState({ showLoading: false });
    });
  }

  changePage(page: number) {
    let allproducts = this.state.resultQueryProducts;
    let returnData = slicePages(allproducts, page, ITEMS_PER_PAGE);
    this.setState({ tableData: returnData });
  }

  renderResultsFilterFields() {
    return (
      <FilterCard
        fields={[
          {
            label: 'Name',
            key: 'ID',
            type: FIELD_TYPE.SELECT,
            options: getLabels(this.state.products, 'products'),
            isMapped: true,
            isMulti: true,
            textType: 'text',
            isFilter: true,
          },
          {
            label: 'Is Home Kit?',
            key: 'IsHomeKit',
            type: FIELD_TYPE.SELECT,
            options: getBoolSelectOptionsWithInts(),
            isMapped: true,
            isMulti: true,
            textType: 'text',
            isFilter: true,
          },
        ]}
        filterChanged={(e) => this.setState({ filter: e })}
      />
    );
  }

  useSorter(col: Column) {
    let sorter = new Sorter<any>();

    this.setState({
      tableData: sorter.sortByKey(this.state.tableData, col.key as keyof any, this.state.direction),
      direction: this.state.direction === 'asc' ? 'desc' : 'asc',
    });
  }

  render() {
    // console.log('ProductsManagement STATE', this.state)
    return (
      <React.Fragment>
        <PaginationTool />
        <Overlay show_loading={this.state.showLoading} />
        <ProductManagementModal
          key={
            this.state.selectedProduct && this.state.selectedProduct.ID
              ? this.state.selectedProduct.ID
              : `new${new Date().getTime()}`
          }
          pageCustomQuestions={this.state.pageCustomQuestions}
          selectedProduct={this.state.selectedProduct}
          onSubmit={(product) => {
            hideModal(ProductManagementModal.ID);
            this.createOrModifyProduct(product);
          }}
          services={this.state.services}
        />

        <div className="container-fluid ">
          <div className={'row'}>
            <div className="col-12 col-md-12 col-lg-8 pt-2">
              <main id="main-content" tabIndex={-1} aria-label="Result Management">
                <div className="card mb-2">
                  <div className="card-header verlag-bold">
                    <h3>Product Management</h3>
                  </div>
                  <div className="card-body">{this.renderResultsFilterFields()}</div>
                  <div className="card-footer">
                    <button
                      className={'btn immySubmitButtonOutline'}
                      onClick={() => {
                        this.setState(
                          {
                            selected_page: { label: 1, value: 1 },
                          },
                          () => {
                            this.queryProductsData(this.state.selected_page.value);
                          }
                        );
                      }}
                    >
                      Search
                    </button>
                    <button
                      className="btn btn-outline-success float-right"
                      onClick={() =>
                        this.setState(
                          {
                            selectedProduct: {
                              ID: null,
                              Name: '',
                              Price: null,
                              Description: null,
                              Units: null,
                            } as Product,
                          },
                          () => {
                            showModalNoOutsideClick(ProductManagementModal.ID);
                          }
                        )
                      }
                    >
                      Create New
                    </button>
                  </div>
                </div>
              </main>
            </div>
            {this.state.tableData && this.state.tableData.length > 0 && (
              <div className="col-12 col-md-12 col-lg-12 pt-2">
                <div className="card mt-2" style={{ marginBottom: '4rem' }}>
                  <div className="card-header verlag-bold stickToTop">
                    <h4 className="text-center text-md-left">
                      Products
                      <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                        <h4 className={'float-md-right'} aria-label="Total Records" role="alert">
                          Total: {this.state.resultQueryProducts[0].length}
                        </h4>
                        <h4 className="d-inline-block float-right align-middle pr-2 ml-5">Page </h4>
                        <div className=" align-middle float-right pages ">
                          <Select
                            isSearchable={true}
                            placeholder={'1'}
                            noOptionsMessage={() => 'No option'}
                            value={this.state.selected_page}
                            aria-label="Table Page Number"
                            onChange={(e: ReactSelect) =>
                              this.setState({ selected_page: e }, () => this.changePage(e.value))
                            }
                            className={'state_select'}
                            options={this.state.page_options}
                          />
                        </div>
                      </section>
                      <button
                        className={'d-none d-md-inline btn btn-outline-primary ml-3'}
                        onClick={() => this.handleExportToCSV()}
                      >
                        Export to CSV
                      </button>
                    </h4>
                  </div>
                  <div className="card-body p-0 m-0 table-responsive">
                    <SimpleTable
                      columns={[
                        {
                          label: 'Product',
                          key: 'Name',
                          popoverText: 'Click to Sort By Products',
                          rawFormat: (val) => {
                            return (
                              <a
                                href={'#'}
                                className={'tableNameLinkColor'}
                                onClick={() => {
                                  this.setState({ selectedProduct: val }, () =>
                                    showModalNoOutsideClick(ProductManagementModal.ID)
                                  );
                                }}
                              >
                                {val.Name}
                              </a>
                            );
                          },
                        },

                        {
                          label: 'Description',
                          key: 'Description',
                          popoverText: 'Click to Sort By Description',
                        },
                        {
                          label: 'Price',
                          key: 'Price',
                          popoverText: 'Click to Sort By Price',
                          rawFormat: (val) => {
                            return <p>{val.Price ? formatCurrencyWithoutDecimals(val.Price) : 'None'}</p>;
                          },
                        },
                        {
                          label: 'Units',
                          key: 'Units',
                          popoverText: 'Click to Sort By Units',
                        },
                        {
                          label: 'Is Home Kit',
                          key: 'IsHomeKit',
                          popoverText: 'Click to Sort By Is Home Kit',
                          rawFormat: (val) => {
                            return <p>{val.IsHomeKit ? 'Yes' : 'No'}</p>;
                          },
                        },
                        {
                          label: 'Service ID',
                          key: 'ServiceID',
                          popoverText: 'Click to Sort By Service ID',
                          rawFormat: (val) => {
                            //@ts-ignore
                            const type =
                              val.ServiceID && this.state.services?.length > 0
                                ? this.state.services.find((k) => k.ID === val.ServiceID)
                                : null;
                            return <p>{type ? type.Name : 'N/A'}</p>;
                          },
                        },
                      ]}
                      table_data={this.state.tableData}
                      columnClickedCallback={(col) => {
                        this.useSorter(col);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}
